// @ts-strict

import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ISingleCampaign, BaseApiResponse, IPremiumSMSCampaignPaymentConfig, DefaultApiType, DefaultApiMessageResponse } from '@app/core/models/core-models';
import { ICampaignDetailsStatistics, ICurrentStatisticsPeriod, IStatisticsFilter } from '../statistics/statistics.types';
import { map } from 'rxjs/operators';
import { CampaignRatingParameters, CampaignRatingResponse, ReportCampaignBugResponse } from './campaign-details.service.types';

@Injectable({
  providedIn: 'root'
})
export class CampaignDetailsService {
  public campaignStats: IStatisticsFilter[] = [];
  public currentCampaign: ISingleCampaign = {};
  public campaignStatsSubject = new Subject<IStatisticsFilter[]>();
  public shareCurrentStatsPeriod = new Subject<ICurrentStatisticsPeriod>();
  public statsPeriod: ICurrentStatisticsPeriod = {
    period: '',
    dateFrom: '',
    dateTo: ''
  };

  public refetchCampaignsDetails$ = new Subject<void>();

  private userCampaignRatingSub$ = new BehaviorSubject<number>(5);

  constructor(private readonly http: HttpClient) {}

  public setUserCampaignRating = (rating: number): void => {
    this.userCampaignRatingSub$.next(rating);
  };

  public getUserCampaignRating$ = (): Observable<number> => {
    return this.userCampaignRatingSub$.asObservable();
  };
  private campaignNameAndPreviewUrlSubject$ = new BehaviorSubject<{ campaignName: string; previewUrl: string }>({
    campaignName: '',
    previewUrl: ''
  });

  public resetCampaignNameAndPreviewUrl = () => {
    this.campaignNameAndPreviewUrlSubject$.next({ campaignName: '', previewUrl: '' });
  };

  public getCampaignNameAndPreviewUrl$ = (): Observable<{ campaignName: string; previewUrl: string }> => {
    return this.campaignNameAndPreviewUrlSubject$.asObservable();
  };

  public setCampaignNameAndPreviewUrl = (data: { campaignName: string; previewUrl: string }) => {
    this.campaignNameAndPreviewUrlSubject$.next(data);
  };

  public getCampaignsDetails(id: number | string): Observable<BaseApiResponse<{ campaign?: ISingleCampaign }>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<{ campaign?: ISingleCampaign }>>(environment.apiUrl + `/campaigns/${id}/info`, httpOptions);
  }

  public getCampaignStats(id: number | string, dateFrom: string, dateTo: string): Observable<BaseApiResponse<{ statistics?: ICampaignDetailsStatistics }>> {
    const params = {
      date_from: dateFrom,
      date_to: dateTo
    };

    return this.http.get<BaseApiResponse<{ statistics?: ICampaignDetailsStatistics }>>(environment.apiUrl + `/campaigns/${id}/info/statistics`, { params });
  }

  public reportCampaignBug(programID: number, description: string): Observable<DefaultApiType<ReportCampaignBugResponse>> {
    return this.http.post<DefaultApiType<ReportCampaignBugResponse>>(environment.apiUrl + `/feedback/campaign_error`, {
      program_id: programID,
      description
    });
  }

  public getPaymentConfig(programId: number, step: number): Observable<IPremiumSMSCampaignPaymentConfig | undefined> {
    const params = {
      step
    };
    return this.http
      .get<BaseApiResponse<IPremiumSMSCampaignPaymentConfig>>(environment.apiUrl + `/premium/payment_configuration/${programId}`, { params })
      .pipe(map(res => res.data));
  }

  public storePaymentConfig(requestParams: { config_id: number; step: number; payment: string; prefix: string }): Observable<BaseApiResponse> {
    let params: { config_id: number; step: number; payment: string; prefix?: string } = {
      config_id: requestParams.config_id,
      payment: requestParams.payment,
      step: requestParams.step
    };
    if (requestParams.prefix) {
      params = { ...params, prefix: requestParams.prefix };
    }
    return this.http.post<BaseApiResponse>(environment.apiUrl + `/premium/payment_configuration/${requestParams.config_id}/store`, params);
  }

  public useDefaultPaymentConfig(config_id: number): Observable<BaseApiResponse> {
    return this.http.post<BaseApiResponse>(environment.apiUrl + `/premium/payment_configuration/${config_id}/store_default`, {});
  }

  public getDefaultPaymentConfig(programId: number, step: number): Observable<IPremiumSMSCampaignPaymentConfig> {
    const params = { step };
    return this.http
      .get<BaseApiResponse>(environment.apiUrl + `/premium/default_payment_configuration/${programId}`, { params })
      .pipe(map((res: any) => res.data));
  }

  public storeDefaultPaymentConfig(requestParams: { config_id: number; step: number; payment: string; prefix?: string }): Observable<BaseApiResponse> {
    let params: { config_id: number; step: number; payment: string; prefix?: string } = {
      config_id: requestParams.config_id,
      payment: requestParams.payment,
      step: requestParams.step
    };
    if (requestParams.prefix) {
      params = { ...params, prefix: requestParams.prefix };
    }
    return this.http.post<BaseApiResponse>(environment.apiUrl + `/premium/default_payment_configuration/${requestParams.config_id}/store`, params);
  }

  public useDefaultPaymentConfigForAll(program_id: number): Observable<BaseApiResponse> {
    return this.http.post<BaseApiResponse>(environment.apiUrl + `/premium/default_payment_configuration/${program_id}/store_all_configurations`, {});
  }

  public getCampaignRating = (program_id: number): Observable<BaseApiResponse<CampaignRatingResponse>> => {
    return this.http.get<BaseApiResponse<CampaignRatingResponse>>(environment.apiUrl + `/campaigns/rating/${program_id}/create`);
  };

  public storeCampaignRating = (program_id: number, requestParams: CampaignRatingParameters): Observable<DefaultApiMessageResponse> => {
    const params = { ...requestParams };

    return this.http.post<DefaultApiMessageResponse>(environment.apiUrl + `/campaigns/rating/${program_id}/store`, params);
  };

  public updateCampaignRating = (program_id: number, requestParams: CampaignRatingParameters): Observable<DefaultApiMessageResponse> => {
    const params = { ...requestParams };

    return this.http.put<DefaultApiMessageResponse>(environment.apiUrl + `/campaigns/rating/${program_id}/update`, params);
  };
}
